import { ILoggingConfig } from "@gtmhub/env";
import { storage } from "../storage";
import { sanitizeHref, stringifyMessage } from "./util";
import { ErrorEvent } from "reconnecting-websocket";
import { IRetryRequestConfig } from "@gtmhub/httpInterceptor";
import { generateStatsItem } from "../tracing";
import { globalRetries } from "@gtmhub/bootstrap/http/global-retries";
import { IHttpRejection, ILoggable, LogLevel, LogSource } from "@webapp/core/logging/models/logging.models";

export class LoggingService {
  constructor(private logging: ILoggingConfig, private src: LogSource) {}

  captureConsoleError(): void {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    // log window console error messages to fluentd
    const _onerror = window.onerror;
    window.onerror = function (event: Event | string, source?: string, lineno?: number, colno?: number, error?: Error) {
      self.logError(error);

      if (_onerror && typeof _onerror === "function") {
        _onerror(event, source, lineno, colno, error);
      }
    };
  }

  logMessage(message: unknown, opts: { level: LogLevel; async?: boolean }): void {
    if (!this.logging.enabled) {
      return;
    }

    const payload = {
      ...this.commonLogPayload(message, opts),
      url: sanitizeHref(window.location.href),
      error: false,
      console: true,
    };
    this.log(payload, { async: opts.async });
  }

  logError(error: Error, opts: { async?: boolean } = {}): void {
    if (!this.logging.enabled) {
      return;
    }

    let loggableData = {};
    const loggable = error as unknown as ILoggable;
    if ("loggableData" in loggable) {
      loggableData = loggable.loggableData();
    }

    const payload = {
      ...loggableData,
      ...this.commonLogPayload(error.message, { level: "ERROR" }),
      error: true,
      console: true,
      stack: error ? error.stack : "null",
      name: error.name || "null",
      url: sanitizeHref(window.location.href),
    };
    this.log(payload, { async: opts.async });
  }

  logSocketError(event: ErrorEvent, url: string): void {
    if (!this.logging.enabled) {
      return;
    }

    const message = event.error?.message || event.message;
    const payload = {
      ...this.commonLogPayload(message, { level: "ERROR" }),
      msg: stringifyMessage(event.error?.message) || stringifyMessage(event.message) || "null",
      stack: event.error?.stack || "null",
      name: event.error?.name || "null",
      ws: true,
      error: true,
      console: false,
      url,
    };
    this.log(payload);
  }

  logHttpError(rejection: IHttpRejection): void {
    if (!this.logging.enabled) {
      return;
    }

    const config = <IRetryRequestConfig>(rejection.config || {});
    const headers = config.headers || {};
    delete headers.Authorization;

    const payload = {
      ...this.commonLogPayload(rejection.data, { level: "ERROR" }),
      msg: stringifyMessage(rejection.data) || "null",
      error: true,
      console: false,
      httpRequest: {
        method: config.method,
        headers,
        jaegerData: generateStatsItem(
          headers["jaeger-baggage"] || "sessionID=<unknown>",
          headers["jaeger-baggage-screen"] || "screenID=<unknown>, screenName=<unknown>",
          config
        ),
      },
      httpResponse: {
        statusCode: rejection.status,
        retries: config.retries,
        globalRetries: globalRetries(),
        xhrStatus: rejection.xhrStatus,
      },
      url: sanitizeHref(window.location.href),
      transactionId: headers["transaction-id"] || "Error: Add transactionId",
    };
    this.log(payload);
  }

  private log(payload: unknown, opts: { async?: boolean } = {}): void {
    try {
      const async = opts.async !== false;
      const xmlHttp = new XMLHttpRequest();
      xmlHttp.open("POST", this.logging.endpoint, async);
      xmlHttp.setRequestHeader("Content-Type", "application/json");
      xmlHttp.send(JSON.stringify(payload));
    } catch (ex) {
      if (window && window.console && typeof window.console.log === "function") {
        console.warn("Failed to log to fluentd because of this exception:\n" + ex);
        console.warn("Failed log data:", payload);
      }
    }
  }

  private commonLogPayload(message: unknown, opts: { level: LogLevel }): Record<string, unknown> {
    return {
      accountId: storage.get("accountId") || "null",
      userId: storage.get("userId") || "null",
      userAgent: window.navigator ? window.navigator.userAgent : "null",
      tags: { ...this.logging.tags, src: this.src },
      level: opts.level,
      msg: stringifyMessage(message) || "null",
    };
  }
}
